import { useContext, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import EditableList from '../EditableList/EditableList';
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import useSpecialDay from '../../hooks/useSpecialDay';
import { toast } from 'react-toastify';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function SpecialDayManagement() {
  const authCtx = useContext(AuthContext);
  const { dryRunGenerate, importGenerate } = useSpecialDay();
  const [showDryRun, setShowDryRun] = useState(false);
  const [hidePast, setHidePast] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dryRunResult, setDryRunResult] = useState(null);

  if (!authCtx.checkRight('specialDay:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const specialDaySchema = useMemo(
    () => ({
      startDate: {
        label: 'Start',
        api: { itemField: 'startDate' },
        type: 'date',
        required: true,
        minWidth: '140px',
      },
      endDate: {
        label: 'Ende',
        api: { itemField: 'endDate' },
        type: 'date',
        required: true,
        minWidth: '140px',
      },
      specialType: {
        label: 'Typ',
        type: 'select',
        options: [
          { value: 'holiday', label: 'Feiertag' },
          { value: 'vacation', label: 'Ferien' },
          { value: 'summerVacation', label: 'Sommerferien' },
          { value: 'bridgeDay', label: 'Brückentag' },
        ],
        required: true,
        minWidth: '160px',
      },
      stateCodes: {
        label: 'Bundesländer',
        type: 'multi-select',
        options: [
          { value: 'BW', label: 'Baden-Württemberg' },
          { value: 'BY', label: 'Bayern' },
          { value: 'BE', label: 'Berlin' },
          { value: 'BB', label: 'Brandenburg' },
          { value: 'HB', label: 'Bremen' },
          { value: 'HH', label: 'Hamburg' },
          { value: 'HE', label: 'Hessen' },
          { value: 'MV', label: 'Mecklenburg-Vorpommern' },
          { value: 'NI', label: 'Niedersachsen' },
          { value: 'NW', label: 'Nordrhein-Westfalen' },
          { value: 'RP', label: 'Rheinland-Pfalz' },
          { value: 'SL', label: 'Saarland' },
          { value: 'SN', label: 'Sachsen' },
          { value: 'ST', label: 'Sachsen-Anhalt' },
          { value: 'SH', label: 'Schleswig-Holstein' },
          { value: 'TH', label: 'Thüringen' },
        ],
        required: false,
        minWidth: '200px',
      },
      description: {
        label: 'Beschreibung',
        api: { itemField: 'description' },
        type: 'text',
        required: false,
        minWidth: '200px',
      },
      assignedEntertainments: {
        label: 'Zugeordnete Entertainments',
        type: 'multi-select',
        api: {
          fieldName: 'entertainment',
          itemField: 'assignedEntertainments',
          idName: 'entertainmentId',
          resource: 'entertainment',
          labelField: 'longName',
          valueField: '_id',
          skipSpecial: true,
        },
        placeholder: 'Entertainments auswählen',
        required: false,
        transformValue: (selectedValues, options) => {
          const array = Array.isArray(selectedValues)
            ? selectedValues
            : [selectedValues].filter(Boolean);
          return array.map(val => {
            const found = (options || []).find(opt => opt.value === val);
            return { entertainmentId: found ? found.value : val, name: found ? found.label : '' };
          });
        },
        listDisplayValue: values => {
          if (!Array.isArray(values) || values.length === 0) return '—';
          return values
            .map(v => v?.name || '')
            .filter(Boolean)
            .join(', ');
        },
      },
    }),
    []
  );

  const handleDryRun = async () => {
    try {
      const data = await dryRunGenerate(year);
      setDryRunResult(data?.result || null);
      toast.info('Dry-Run abgeschlossen');
      setShowDryRun(true);
    } catch (e) {
      toast.error('Fehler beim Dry-Run');
    }
  };

  const handleImport = async () => {
    try {
      await importGenerate(year);
      setDryRunResult(null);
      toast.success('Import ausgelöst');
      // trigger list refresh by reloading page in EditableList via search term change event
      window.dispatchEvent(
        new CustomEvent('editableListRefresh', { detail: { resource: 'specialDay' } })
      );
    } catch (e) {
      toast.error('Fehler beim Import');
    }
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
      {authCtx.checkRight('specialDay:create') && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              label='Jahr'
              type='number'
              size='small'
              value={year}
              onChange={e => setYear(parseInt(e.target.value || '0', 10))}
              sx={{ width: 120 }}
            />
            <Button
              variant={hidePast ? 'contained' : 'outlined'}
              onClick={() => setHidePast(prev => !prev)}
            >
              {hidePast ? 'Vergangenes ausblenden' : 'Vergangenes einblenden'}
            </Button>
            <Button variant='outlined' onClick={handleDryRun}>
              DryRun anzeigen
            </Button>
            <Button variant='contained' color='success' onClick={handleImport}>
              Import starten
            </Button>
          </Box>
          <Dialog open={showDryRun} onClose={() => setShowDryRun(false)} maxWidth='md' fullWidth>
            <DialogTitle>Dry-Run Vorschau</DialogTitle>
            <DialogContent dividers>
              <Typography variant='body2' sx={{ mb: 1 }}>
                {dryRunResult ? (
                  <>
                    Vorschau: {dryRunResult.items?.length || 0} Einträge (
                    {dryRunResult.created || 0} neu, {dryRunResult.updated || 0} aktualisiert)
                  </>
                ) : (
                  'Keine Daten'
                )}
              </Typography>
              {Array.isArray(dryRunResult?.items) && (
                <Box component='pre' sx={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                  {dryRunResult.items
                    .slice(0, 500)
                    .map(
                      (it, idx) =>
                        `${idx + 1}. ${it.type} ${it.date || `${it.start} - ${it.end}`} – ${
                          it.description || ''
                        } [${it.stateCode || ''}]`
                    )
                    .join('\n')}
                  {dryRunResult.items.length > 500 ? '\n…' : ''}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowDryRun(false)}>Schließen</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <EditableList
        schema={specialDaySchema}
        title='Spezialtage verwalten'
        resourceType='specialDay'
        headerSearchPlaceholder='Sondertage durchsuchen'
        parentIsEditable={authCtx.checkRight('specialDay:update')}
        options={{ titleKey: 'description' }}
        visibleColumnKeys={[
          'startDate',
          'endDate',
          'specialType',
          'stateCodes',
          'description',
          'assignedEntertainments',
        ]}
        listParams={{ hidePast }}
      />
    </Box>
  );
}

export default SpecialDayManagement;
